import { keyframes } from '@piiqtechnologies/ui-components';

// How one pop-up slide hands over to the next, inside the one card. The old slide's colours fade
// out over the new ones while its picture and words drift left, and the new picture, then words,
// come in from the right. The card takes the new slide's height (a slide without a picture is
// shorter), and the old slide is clipped to it while it fades. Focus stays on "Next". Nothing moves
// for anyone who asked for less motion.
/** How long the old slide stays over the new one. */
const SLIDE_EXIT_MS = 320;
const SLIDE_ENTER_MS = 480;
/** The new picture waits for the old one to be mostly gone, so the two barely overlap. */
const ENTER_DELAY_MS = 140;
/** The words follow the picture by this much. */
const STAGGER_MS = 70;
const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_IN = "cubic-bezier(0.4, 0, 1, 1)";
const fadeOut = keyframes `
  from { opacity: 1; }
  to { opacity: 0; }
`;
const driftOut = keyframes `
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-24px); }
`;
const driftIn = keyframes `
  from { opacity: 0; transform: translateX(32px); }
  to { opacity: 1; transform: translateX(0); }
`;
const fadeScaleIn = keyframes `
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
`;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
/** For an `sx`: what to switch off for anyone who asked for less motion. */
const REDUCED_MOTION = `@media ${REDUCED_MOTION_QUERY}`;
/** The old slide's colours, laid over the new ones: they fade out. */
const surfaceMotion = (motion) => motion === "exit"
    ? { animation: `${fadeOut} ${SLIDE_EXIT_MS}ms ${EASE_IN} forwards`, [REDUCED_MOTION]: { animation: "none" } }
    : {};
/** The picture (`order` 0) and the words (`order` 1): out to the left, or in from the right. */
const contentMotion = (motion, order) => {
    if (!motion)
        return {};
    const animation = motion === "exit"
        ? `${driftOut} ${SLIDE_EXIT_MS}ms ${EASE_IN} forwards`
        : `${driftIn} ${SLIDE_ENTER_MS}ms ${EASE_OUT} ${ENTER_DELAY_MS + order * STAGGER_MS}ms both`;
    return { animation, [REDUCED_MOTION]: { animation: "none" } };
};
/** A view swapped in place (the demo player, the card again after it). */
const viewEnterMotion = {
    animation: `${fadeScaleIn} 280ms ${EASE_OUT}`,
    [REDUCED_MOTION]: { animation: "none" },
};
/** Whether slides may animate. Where it can't be asked (tests, old browsers), they don't. */
const prefersMotion = () => typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    !window.matchMedia(REDUCED_MOTION_QUERY).matches;

export { REDUCED_MOTION, SLIDE_EXIT_MS, contentMotion, prefersMotion, surfaceMotion, viewEnterMotion };
